import { memo, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { HandleModalProps } from "@/types/props";
import { emptyMasterDegree } from "@/models/empty";

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ZodError } from "../ZodDisplayError";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { CalendarIcon, ChevronDown, RefreshCw } from "lucide-react";
import { Calendar } from "../ui/calendar";
import { masterDegreeSchema, tMasterDegreeSchema } from "../validations/masterDegreee";
import { ethnicCodeAndNameList, majorCodesWithName, nationalityCodeAndNameList, requestTypeList, trainingMethodList } from "../validations/shared";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { cn } from "../ui/utils";
import { NgocInput } from "../ui/ngocInput";
import { getLocalDate } from "@/lib/utils/func";

export const MasterDegreeModal = memo(({ isOpen, onClose, onAdd, editData, onUpdate, mode }: HandleModalProps) => {
	const {
		control,
		register,
		handleSubmit,
		reset,
		trigger,
		setValue,
		getValues,
		formState: { errors, isValid },
	} = useForm<tMasterDegreeSchema>({
		resolver: zodResolver(masterDegreeSchema),
		defaultValues: emptyMasterDegree,
		mode: "all",
	});
	const submit = (data: tMasterDegreeSchema) => {
		switch (mode) {
			case "create":
				onAdd(data);
				break;
			case "edit":
				onUpdate(data);
				break;
		}
		onClose();
	};

	useEffect(() => {
		switch (mode) {
			case "create":
				reset(emptyMasterDegree);
				break;
			case "edit":
				reset({
					...editData,
					currentDegreeNumber: editData.degreeNumber || "",
					currentCCCD: editData.cccd || "",
					currentMajorCode: editData.majorCode || "",
					currentStudentId: editData.studentId || "",
					ethnicCode: editData.ethnicCode || "",
					ethnicity: editData.ethnicity || "",
					nationalityCode: editData.nationalityCode || "",
					nationality: editData.nationality || "",
					classificationId: editData.classificationId || "",
					signerTitle: editData.signerTitle || "",
					recognitionDecisionNumber: editData.recognitionDecisionNumber || "",
					requestType: editData.requestType || "",
					notes: editData.notes || "",
					graduationYear: editData.graduationYear.toString() || "",
					dateOfBirth: editData.dateOfBirth ? getLocalDate(editData.dateOfBirth) : emptyMasterDegree.dateOfBirth,
					signDate: editData.signDate ? getLocalDate(editData.signDate) : emptyMasterDegree.signDate,
					defenseDate: editData.defenseDate ? getLocalDate(editData.defenseDate) : emptyMasterDegree.defenseDate,
				});
				trigger();
				break;
		}
	}, [isOpen, editData, mode, reset, trigger]);

	const onError = (e: any) => {
		console.log(e);
	};

	const [openDateOfBirthSelected, setOpenDateOfBirthSelected] = useState(false);
	const [openSignDateSelected, setOpenSignDateSelected] = useState(false);
	const [openDefenseDate, setOpenDefenseDate] = useState(false);
	const [searchMajorName, setSearchMajorName] = useState("");
	const years = useMemo(() => {
		const list = [];
		for (let y = 1995; y <= new Date().getFullYear(); y++) list.unshift(y);
		return list;
	}, []);
	return (
		<Dialog
			open={isOpen}
			onOpenChange={() => {
				onClose();
				setSearchMajorName("");
			}}
		>
			<DialogContent className='max-w-6xl'>
				<DialogHeader>
					<DialogTitle>{mode === "create" ? "Thêm Mới" : "Cập Nhật Bằng Thạc Sĩ"}</DialogTitle>
					{/* <DialogDescription>Thêm thông tin văn bằng thạc sĩ mới vào hệ thống</DialogDescription> */}
				</DialogHeader>
				<form onSubmit={handleSubmit(submit, onError)} id='handleMasterDegreeForm' className='grid grid-cols-2 gap-2 px-1 max-h-[75dvh] overflow-y-auto'>
					{/* Thông tin cá nhân */}
					<div className='col-span-2'>
						<h3 className='font-semibold text-lg text-red-600'>Thông tin cá nhân</h3>
					</div>
					<div className='space-y-1'>
						<Label className='required'>Họ và tên</Label>
						<NgocInput placeholder='Nhập họ tên' maxLength={250} {...register("studentName")} />
						<ZodError message={errors.studentName?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Mã sinh viên </Label>
						<NgocInput placeholder='Nhập Mã sinh viên' maxLength={250} {...register("studentId")} />
						<ZodError message={errors.studentId?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số CCCD </Label>
						<NgocInput placeholder='Nhập số CCCD' maxLength={12} {...register("cccd")} />
						<ZodError message={errors.cccd?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngày sinh </Label>
						<Controller
							control={control}
							name='dateOfBirth'
							render={({ field }) => (
								<Popover open={openDateOfBirthSelected} onOpenChange={(value) => { setOpenDateOfBirthSelected(value); if (!value) trigger("dateOfBirth"); }}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày sinh" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												if (value) {
													field.onChange(getLocalDate(value));
													setOpenDateOfBirthSelected(false);
												}
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>
						<ZodError message={errors.dateOfBirth?.message} />
					</div>
					<div className='space-y-1'>
						<Label className=''>Nơi sinh </Label>
						<NgocInput placeholder='Nhập nơi sinh' maxLength={250} {...register("placeOfBirth")} />
						<ZodError message={errors.placeOfBirth?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Giới tính </Label>
						<Controller
							control={control}
							name='gender'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn giới tính' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Nam'>Nam</SelectItem>
										<SelectItem value='Nữ'>Nữ</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.gender?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Dân tộc</Label>
						<Controller
							control={control}
							name='ethnicCode'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const targetEthnic = ethnicCodeAndNameList.find((x) => x.code == value);
										setValue("ethnicity", targetEthnic!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn dân tộc' />
									</SelectTrigger>
									<SelectContent className='h-64 overflow-y-scroll'>
										{ethnicCodeAndNameList.map((et) => {
											return (
												<SelectItem key={et.code} value={et.code}>
													({et.code}) {et.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.ethnicCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Quốc tịch</Label>
						<Controller
							control={control}
							name='nationalityCode'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const targetNation = nationalityCodeAndNameList.find((x) => x.code == value);
										setValue("nationality", targetNation!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn quốc tịch' />
									</SelectTrigger>
									<SelectContent className='h-64 overflow-y-scroll'>
										{nationalityCodeAndNameList.map((et) => {
											return (
												<SelectItem key={et.code} value={et.code}>
													({et.code}) {et.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.nationalityCode?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label>Mã Dân tộc</Label>
						<NgocInput placeholder='Nhập mã dân tộc' {...register("ethnicCode")} />
						<ZodError message={errors.ethnicCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Dân tộc</Label>
						<NgocInput placeholder='Nhập tên dân tộc' {...register("ethnicity")} />
						<ZodError message={errors.ethnicity?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Mã quốc tịch</Label>
						<NgocInput placeholder='Nhập mã quốc tịch' {...register("nationalityCode")} />
						<ZodError message={errors.nationalityCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Quốc tịch</Label>
						<NgocInput placeholder='Nhập quốc tịch' {...register("nationality")} />
						<ZodError message={errors.nationality?.message} />
					</div> */}

					{/* Thông tin văn bằng */}
					<div className='col-span-2 mt-4'>
						<h3 className='font-semibold text-lg text-red-600'>Thông tin văn bằng</h3>
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngành học</Label>
						{/* <Input placeholder='Nhập mã ngành' {...register("majorCode")} autoComplete='off' /> */}
						<Controller
							control={control}
							name='majorCode'
							render={({ field }) => (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant='outline' className={cn("w-full justify-between text-slate-700 font-normal")}>
											{getValues("majorCode") == "" ? "Chọn ngành" : `(${getValues("majorCode")}) ${getValues("major")}`}
											<ChevronDown />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										<div className='flex gap-1'>
											<input
												value={searchMajorName}
												onInput={(e: any) => {
													setSearchMajorName(e.target.value);
												}}
												type='text'
												className='border w-full px-1'
												placeholder='Tìm kiếm...'
												onKeyDown={(e) => {
													e.stopPropagation();
												}}
											/>
											<button
												onClick={() => {
													setSearchMajorName("");
												}}
												type='button'
												className='cursor-pointer text-sm'
											>
												<RefreshCw size={"1rem"} />
											</button>
										</div>
										<div className='h-40 overflow-y-scroll'>
											{majorCodesWithName
												.filter((mj) => mj.name.toLocaleLowerCase().includes(searchMajorName.trim().toLowerCase()))
												.map((mj) => {
													return (
														<DropdownMenuCheckboxItem
															key={mj.code}
															checked={getValues("majorCode") === mj.code}
															onCheckedChange={() => {
																field.onChange(mj.code);
																const targetMajor = majorCodesWithName.find((smj) => smj.code === mj.code);
																setValue("major", targetMajor!.name);
																trigger("cccd");
															}}
														>
															({mj.code}) {mj.name}
														</DropdownMenuCheckboxItem>
													);
												})}
										</div>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						/>

						<ZodError message={errors.majorCode?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label className='required'>Ngành</Label>
						<Controller
							control={control}
							name='major'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn ngành đào tạo' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Công nghệ Thông tin'>Công nghệ Thông tin</SelectItem>
										<SelectItem value='Quản trị Kinh doanh'>Quản trị Kinh doanh</SelectItem>
										<SelectItem value='Kế toán'>Kế toán</SelectItem>
										<SelectItem value='Ngôn ngữ Anh'>Ngôn ngữ Anh</SelectItem>
										<SelectItem value='Xây dựng'>Xây dựng</SelectItem>
										<SelectItem value='Cơ khí'>Cơ khí</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.major?.message} />
					</div> */}
					<div className='space-y-1'>
						<Label className='required'>Ngày bảo vệ</Label>
						<Controller
							control={control}
							name='defenseDate'
							render={({ field }) => (
								<Popover open={openDefenseDate} onOpenChange={setOpenDefenseDate}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày bảo vệ" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												field.onChange(getLocalDate(value));
												setOpenDefenseDate(false);
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>
						<ZodError message={errors.defenseDate?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Hình thức đào tạo</Label>
						<Controller
							control={control}
							name='trainingMode'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn hình thức' />
									</SelectTrigger>
									<SelectContent>
										{trainingMethodList.map((me) => {
											return (
												<SelectItem key={me.code} value={me.code}>
													{me.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.trainingMode?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số hiệu bằng (cơ sở)</Label>
						<NgocInput placeholder='Nhập số liệu văn bằng' maxLength={250} {...register("degreeNumber")} />
						<ZodError message={errors.degreeNumber?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số vào sổ cấp bằng</Label>
						<NgocInput placeholder='Nhập số vào sổ câp văn bằng' maxLength={50} {...register("registrationNumber")} />
						<ZodError message={errors.registrationNumber?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Người ký</Label>
						<NgocInput placeholder='Nhập tên người ký' maxLength={250} {...register("signedBy")} />
						<ZodError message={errors.signedBy?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Chức danh người ký</Label>
						<NgocInput placeholder='Nhập chức danh người ký' maxLength={250} {...register("signerTitle")} />
						<ZodError message={errors.signerTitle?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngày ký </Label>
						<Controller
							control={control}
							name='signDate'
							render={({ field }) => (
								<Popover open={openSignDateSelected} onOpenChange={(value) => { setOpenSignDateSelected(value); if (!value) trigger("signDate"); }}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày ký" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												if (value) {
													field.onChange(getLocalDate(value));
													setOpenSignDateSelected(false);
												}
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>
						<ZodError message={errors.signDate?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Khoá học</Label>
						<NgocInput placeholder='Nhập tên khóa học' maxLength={250} {...register("course")} />
						<ZodError message={errors.course?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Quyết định công nhận số</Label>
						<NgocInput placeholder='Nhập quyết định' maxLength={250} {...register("recognitionDecisionNumber")} />
						<ZodError message={errors.recognitionDecisionNumber?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Loại yêu cầu</Label>
						<Controller
							control={control}
							name='requestTypeId'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const targetRequestType = requestTypeList.find((x) => x.code == value);
										setValue("requestType", targetRequestType!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn loại yêu cầu' />
									</SelectTrigger>
									<SelectContent>
										{requestTypeList.map((rq) => {
											return (
												<SelectItem key={rq.code} value={rq.code}>
													({rq.code}) {rq.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.requestTypeId?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label>Loại yêu cầu</Label>
						<Controller
							control={control}
							name='requestType'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn loại yêu cầu' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Cấp văn bằng gốc'>Cấp văn bằng gốc</SelectItem>
										<SelectItem value='Cấp bằng sao y'>Cấp bằng sao y</SelectItem>
										<SelectItem value='Cấp bằng thay thế'>Cấp bằng thay thế</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.requestType?.message} />
					</div> */}
					<div className='space-y-1'>
						<Label>Trình độ</Label>
						<NgocInput value={"Thạc sĩ"} readOnly disabled />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Năm tốt nghiệp</Label>
						<Controller
							control={control}
							name='graduationYear'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn năm' />
									</SelectTrigger>
									<SelectContent className='h-fit max-h-60 overflow-y-scroll'>
										{years.map((y) => {
											return (
												<SelectItem key={y} value={y.toString()}>
													{y}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.graduationYear?.message} />
					</div>
					<div className='col-span-2 space-y-1'>
						<Label>Ghi chú</Label>
						<NgocInput rows={3} maxLength={500} placeholder='Nhập ghi chú' {...register("notes")} />
						<ZodError message={errors.notes?.message} />
					</div>
				</form>
				<div className='flex justify-end gap-2'>
					<Button
						variant='destructive'
						onClick={() => {
							onClose();
							setSearchMajorName("");
						}}
					>
						Hủy
					</Button>
					<Button disabled={!isValid} variant={"add"} type='submit' form='handleMasterDegreeForm'>
						Lưu
					</Button>
				</div>
			</DialogContent>
		</Dialog >
	);
});
